import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormatters, useI18n } from '../../i18n';
import { NumberField, TextField, CheckboxField } from '../../components/Field';
import { ApiError, countOpenAlex } from '../../lib/api';
import { describeError } from '../../lib/errors';
import type { OpenAlexQuery } from '../../lib/types';

/**
 * The OpenAlex query builder, with the result count kept live.
 *
 * The count is what makes this usable: a query that matches 80,000 works is
 * over the document cap, and the user should see that while editing rather
 * than after waiting for a run to fail. The count comes from the API rather
 * than from the browser so that the filter string counted is exactly the
 * filter string the job will fetch.
 */

export interface QueryFormState {
  search: string;
  yearFrom: number | null;
  yearTo: number | null;
  venue: string;
  openAccess: boolean;
  institution: string;
  country: string;
  filter: string;
  maxResults: number | null;
}

export function emptyQueryState(): QueryFormState {
  return {
    search: '',
    yearFrom: null,
    yearTo: null,
    venue: '',
    openAccess: false,
    institution: '',
    country: '',
    filter: '',
    maxResults: null,
  };
}

/** Drop empty fields so the request body says what the user actually set. */
export function toOpenAlexQuery(state: QueryFormState, maxDocuments: number): OpenAlexQuery {
  const query: OpenAlexQuery = {};
  if (state.search.trim()) query.search = state.search.trim();
  if (state.yearFrom !== null) query.year_from = state.yearFrom;
  if (state.yearTo !== null) query.year_to = state.yearTo;
  if (state.venue.trim()) query.venue = state.venue.trim();
  if (state.openAccess) query.open_access = true;
  if (state.institution.trim()) query.institution = state.institution.trim();
  if (state.country.trim()) query.country = state.country.trim().toUpperCase();
  if (state.filter.trim()) query.filter = state.filter.trim();
  query.max_results = Math.min(state.maxResults ?? maxDocuments, maxDocuments);
  return query;
}

/** A query with nothing in it would match all of OpenAlex; do not count that. */
export function queryIsEmpty(state: QueryFormState): boolean {
  return (
    !state.search.trim() &&
    state.yearFrom === null &&
    state.yearTo === null &&
    !state.venue.trim() &&
    !state.institution.trim() &&
    !state.country.trim() &&
    !state.filter.trim() &&
    !state.openAccess
  );
}

type CountState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; count: number }
  | { kind: 'failed'; error: unknown };

export function OpenAlexQueryBuilder({
  state,
  onChange,
  maxDocuments,
  onCountChange,
}: {
  state: QueryFormState;
  onChange: (next: QueryFormState) => void;
  maxDocuments: number;
  /** Reported upward so the run button can warn before the job starts. */
  onCountChange?: (count: number | null) => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const fmt = useFormatters();
  const [count, setCount] = useState<CountState>({ kind: 'idle' });
  const [nonce, setNonce] = useState(0);

  const set = <K extends keyof QueryFormState>(key: K, value: QueryFormState[K]) =>
    onChange({ ...state, [key]: value });

  // Serialise the query so the effect only re-runs when it really changed.
  const query = useMemo(() => toOpenAlexQuery(state, maxDocuments), [state, maxDocuments]);
  const querySignature = JSON.stringify(query);
  const empty = queryIsEmpty(state);

  const report = useRef(onCountChange);
  report.current = onCountChange;

  useEffect(() => {
    if (empty) {
      setCount({ kind: 'idle' });
      report.current?.(null);
      return;
    }
    const controller = new AbortController();
    // Debounced: typing a search term should not fire a request per keystroke.
    const timer = window.setTimeout(() => {
      setCount({ kind: 'loading' });
      countOpenAlex(JSON.parse(querySignature) as OpenAlexQuery, controller.signal)
        .then((result) => {
          setCount({ kind: 'ready', count: result.count });
          report.current?.(result.count);
        })
        .catch((cause: unknown) => {
          if (cause instanceof ApiError && cause.code === 'cancelled') return;
          setCount({ kind: 'failed', error: cause });
          report.current?.(null);
        });
    }, 500);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [querySignature, empty, nonce]);

  return (
    <div className="stack">
      <TextField
        label={t('query.search.label')}
        hint={t('query.search.hint')}
        placeholder={t('query.search.placeholder')}
        value={state.search}
        onChange={(next) => set('search', next)}
      />

      <div className="grid grid--3">
        <NumberField
          label={t('query.yearFrom.label')}
          hint={t('query.year.hint')}
          optional
          value={state.yearFrom}
          min={1500}
          max={2100}
          step={1}
          onChange={(next) => set('yearFrom', next)}
        />
        <NumberField
          label={t('query.yearTo.label')}
          optional
          value={state.yearTo}
          min={1500}
          max={2100}
          step={1}
          onChange={(next) => set('yearTo', next)}
        />
        <NumberField
          label={t('query.maxResults.label')}
          hint={t('query.maxResults.hint', { cap: fmt.int(maxDocuments) })}
          optional
          placeholder={fmt.int(maxDocuments)}
          value={state.maxResults}
          min={1}
          max={maxDocuments}
          step={100}
          onChange={(next) => set('maxResults', next)}
        />
      </div>

      <div className="grid grid--2">
        <TextField
          label={t('query.venue.label')}
          hint={t('query.venue.hint')}
          placeholder={t('query.venue.placeholder')}
          optional
          value={state.venue}
          onChange={(next) => set('venue', next)}
        />
        <TextField
          label={t('query.institution.label')}
          hint={t('query.institution.hint')}
          placeholder={t('query.institution.placeholder')}
          optional
          value={state.institution}
          onChange={(next) => set('institution', next)}
        />
        <TextField
          label={t('query.country.label')}
          hint={t('query.country.hint')}
          placeholder={t('query.country.placeholder')}
          optional
          value={state.country}
          onChange={(next) => set('country', next)}
        />
        <TextField
          label={t('query.filter.label')}
          hint={t('query.filter.hint')}
          placeholder={t('query.filter.placeholder')}
          optional
          value={state.filter}
          onChange={(next) => set('filter', next)}
        />
      </div>

      <CheckboxField
        label={t('query.openAccess.label')}
        hint={t('query.openAccess.hint')}
        checked={state.openAccess}
        onChange={(next) => set('openAccess', next)}
      />

      <ResultCount
        state={count}
        maxDocuments={maxDocuments}
        onRetry={() => setNonce((n) => n + 1)}
      />
    </div>
  );
}

function ResultCount({
  state,
  maxDocuments,
  onRetry,
}: {
  state: CountState;
  maxDocuments: number;
  onRetry: () => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const fmt = useFormatters();

  if (state.kind === 'idle') {
    return <p className="field__hint">{t('count.idle')}</p>;
  }

  if (state.kind === 'loading') {
    return (
      <p className="field__hint" role="status" aria-live="polite">
        {t('count.loading')}
      </p>
    );
  }

  if (state.kind === 'failed') {
    const { message } = describeError(state.error, i18n);
    return (
      <div className="row">
        <p className="field__error">{t('count.error', { message })}</p>
        <button type="button" className="button button--quiet" onClick={onRetry}>
          {t('count.refresh')}
        </button>
      </div>
    );
  }

  if (state.count === 0) {
    return (
      <div className="callout callout--warn" role="status">
        <p>{t('count.zero')}</p>
      </div>
    );
  }

  const overCap = state.count > maxDocuments;

  return (
    <div className={overCap ? 'callout callout--warn' : 'callout callout--accent'} role="status">
      <p className="nums">
        {overCap
          ? t('count.capped', { count: fmt.int(state.count), cap: fmt.int(maxDocuments) })
          : t('count.result', { count: fmt.int(state.count) })}
      </p>
    </div>
  );
}
